/**
 * CAOS-211 — the install boundary seam.
 *
 * The empty-workspace picker installs a package by calling THIS seam, never the Engine's
 * in-process `installPackage` directly (that is Engine-internal — it needs a metadata store, a
 * transactor, and a DB). The boundary mirrors the existing host seams (`SignOutHandler`,
 * `fetchKernelDecision`):
 *
 *   • the DEFAULT implementation POSTs `{ packageId }` to `/api/packages/install` — same-origin,
 *     the exact Backend-for-Frontend shape as `/api/boot/surface` (`host/src/worker.ts` strips
 *     `/api` and re-injects the session bearer). That endpoint is the CAOS-244 server-side gap;
 *     when it mounts, this client lights up end-to-end with no change here.
 *   • the seam is OVERRIDABLE ({@link setInstallClient}) so the deterministic component test
 *     injects a mock boundary and the dev fixture injects an in-memory client.
 *
 * The seam is deliberately per-package (one `installPackage` call = one package + its members on
 * the Engine side); a catalog entry that spans two packages (the workspace) drives two calls.
 */

import { changedNothing } from '../kernel-noop.js';

/** The path the default client POSTs to (proxied to the Engine's `packages/install`, CAOS-244). */
export const PACKAGES_INSTALL_PATH = '/api/packages/install';

/**
 * CAOS-401 — the path the preview reads (proxied to the Engine's `packages/install-preview`).
 *
 * A different URL rather than a flag on the one above, which is how the Engine models it too: what
 * decides whether anything is written is which endpoint was hit, so no body field can turn a look
 * into an install. A kernel that predates the preview answers `not_found.route`, which this client
 * reports as "could not be read" rather than guessing at an answer.
 */
export const PACKAGES_INSTALL_PREVIEW_PATH = '/api/packages/install-preview';

/**
 * CAOS-1728 — the path that installs by UNIVERSAL KEY (the Engine's `packages/install-by-key`).
 *
 * A third URL for the same reason the preview is a second one: what a request can do is decided by
 * which endpoint it reached, not by a field in its body. `/api/packages/install` resolves a key the
 * worker was compiled with; this one resolves the global published-version registry. Collapsing
 * them into one route with a discriminating field would mean a malformed or mistaken body could
 * reach the wrong resolver, and the two have different authority.
 *
 * A kernel that predates the route answers `not_found.route`, which {@link installByKey} surfaces
 * as an `ok:false` outcome CARRYING THAT CODE — never a silent fall-through to the other door,
 * which would turn "this platform cannot install by key" into "no installable package is
 * registered for pkg_ws_slate" and send the next reader to the wrong registry.
 *
 * Precisely, because the distinction matters to whoever is reading a failure: the outcome's `code`
 * is the kernel's (`not_found.route`) and its `error` is `Install failed (<the kernel's message>)`.
 * Neither names this path. A caller that needs to tell the two doors apart in a message has the
 * code to do it with, and the constant above to compare against.
 */
export const PACKAGES_INSTALL_BY_KEY_PATH = '/api/packages/install-by-key';

/** The outcome of installing one package. `ok:false` carries a human-readable `error`. */
export interface InstallOutcome {
  readonly ok: boolean;
  /** The minted generation on success (when the Engine reports it), for surfacing/telemetry. */
  readonly version?: number;
  /** A human-readable failure message when `ok` is false. */
  readonly error?: string;
  /** The Engine/transport error code when available (e.g. `validation.*`, `integration.engine_unreachable`). */
  readonly code?: string;
  /**
   * CAOS-1715 — the write was accepted and changed nothing: the package was already installed at
   * this version. `ok: true` with `noop: true` is a success, it is not a failure, and it is not an
   * install.
   *
   * Optional because a kernel that predates the convention on a route simply omits the field, and
   * absent has to read as "not known to be a no-op" rather than as one. Undefined and `false` mean
   * the same thing to every caller; only `true` makes a claim.
   */
  readonly noop?: boolean;
}

/**
 * CAOS-401 — one declared prerequisite, as the Engine reports it.
 *
 * Mirrors the kernel's `ResolvedPrerequisite`. Held as its own local shape rather than imported so
 * the host keeps reading the wire as a wire, the way every other boundary type here does.
 */
export interface Prerequisite {
  /** The package that is required. */
  readonly package: string;
  /** The range the declaring package asked for. */
  readonly range: string;
  /** Which package declared it — "who needs this", for a reader asking why. */
  readonly imposedBy: string;
  /** The version resolution settled on, or null when nothing satisfies it. */
  readonly version: string | null;
  /** Whether the workspace already holds it, this install brings it, or nothing satisfies it. */
  readonly status: 'already-installed' | 'will-install' | 'unresolved';
}

/** The outcome of previewing one package. `ok:false` carries a human-readable `error`. */
export interface PreviewOutcome {
  readonly ok: boolean;
  /** Every declared prerequisite of the package, the already-met ones included. */
  readonly prerequisites?: readonly Prerequisite[];
  readonly error?: string;
  readonly code?: string;
}

/** The install boundary the picker drives. One call installs one package. */
export interface InstallClient {
  install(packageId: string): Promise<InstallOutcome>;
  /**
   * Read what a package requires WITHOUT installing it. Optional on the interface: the dev fixture
   * and the component tests inject their own boundaries, and a client that cannot answer this is
   * a screen that shows no prerequisite line — never a screen that fails to render.
   */
  preview?(packageId: string): Promise<PreviewOutcome>;
  /**
   * CAOS-1728 — install a package by its UNIVERSAL KEY, for a catalog entry whose package is not in
   * the compiled-in standard registry (see `CatalogEntry.universalKeys`).
   *
   * Optional for the same reason `preview` is: the injected boundaries in the dev fixture and the
   * component tests implement what they are asked about. Unlike `preview`, an absent implementation
   * here is NOT a cosmetic degradation — the install cannot happen at all — so the picker reports
   * that as a failure with its own code rather than treating it as nothing to show.
   *
   * `packageId` IS REDUNDANT FOR THE REAL CLIENT AND IS PASSED ANYWAY, which needs justifying.
   * The Engine resolves the key to a package itself and returns `packageKey`, so `httpInstallClient`
   * ignores this argument. An in-memory client cannot: it has to record the install under the key
   * that `isInstalled` and the fixture's reboot read, which is the PACKAGE key, and the only other
   * way to learn it from a `pvk_` is to hold a copy of the key→package map.
   *
   * That copy is what this parameter exists to avoid, and the reason is measured rather than
   * tidiness. The dev fixture lives in `demo/decision-source.ts`, which `main.ts` imports
   * unconditionally — so anything that module imports is in the PRODUCTION bundle regardless of any
   * `import.meta.env.DEV` guard elsewhere. A first version of this had the fixture import the keys
   * map, and a grep of the built bundle found `pvk_` and `pkg_ws_slate` in the shipped JavaScript.
   * The caller already knows the package key (the picker is iterating `entry.packages`), so handing
   * it over costs one argument and keeps the fixture's imports clean.
   */
  installByKey?(universalKey: string, packageId: string): Promise<InstallOutcome>;
}

/** The substrate passthrough envelope, as read off the wire. */
type InstallEnvelope =
  | { ok: true; data?: { version?: number; activeVersion?: number; noop?: boolean } }
  | { ok: false; error?: { message?: string; code?: string } };

/** The same envelope around the preview's answer (CAOS-401). */
type PreviewEnvelope =
  | { ok: true; data?: { prerequisites?: readonly Prerequisite[] } }
  | { ok: false; error?: { message?: string; code?: string } };

/**
 * POST an install request and read the substrate `{ ok, data | error }` envelope.
 *
 * CAOS-1728 extracted this from `install` when `installByKey` arrived, rather than writing the
 * fail-soft reading a second time. The two doors differ only in their path and their request body;
 * everything after the response is identical, and a copy of it would be a copy that drifts — the
 * `noop` convention below is exactly the kind of line that gets updated in one place and not the
 * other, and the second door would then start reporting a real no-op as a real install.
 */
async function postInstall(path: string, body: Record<string, unknown>): Promise<InstallOutcome> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  } catch (err) {
    return {
      ok: false,
      code: 'install.unreachable',
      error: `Could not reach the install endpoint (${err instanceof Error ? err.message : String(err)}).`,
    };
  }

  let envelope: InstallEnvelope;
  try {
    envelope = (await response.json()) as InstallEnvelope;
  } catch {
    return {
      ok: false,
      code: 'install.bad_response',
      error: `The install endpoint returned a non-JSON response (HTTP ${response.status}).`,
    };
  }

  if (!envelope || envelope.ok !== true) {
    const detail =
      envelope && envelope.ok === false && envelope.error?.message
        ? envelope.error.message
        : `HTTP ${response.status}`;
    const code = envelope && envelope.ok === false ? envelope.error?.code : undefined;
    return { ok: false, code, error: `Install failed (${detail}).` };
  }

  // Read off the CONVENTION rather than this route's own field: `changedNothing` is the single
  // reader of the kernel's `noop` flag, shared with the roster surfaces, so a route that starts
  // answering no-op is covered here on arrival instead of needing this line written again.
  return { ok: true, version: envelope.data?.version, noop: changedNothing(envelope.data) };
}

/**
 * The default HTTP client: POST `{ packageId }` to {@link PACKAGES_INSTALL_PATH}, read the
 * substrate `{ ok, data | error }` envelope. Fail-soft — a transport error or a non-JSON body
 * becomes an `ok:false` outcome with a legible message, never a thrown exception the picker
 * would have to catch.
 */
export const httpInstallClient: InstallClient = {
  async install(packageId: string): Promise<InstallOutcome> {
    return postInstall(PACKAGES_INSTALL_PATH, { packageId });
  },

  /**
   * CAOS-1728 — the by-key door. Body `{ universalKey }`, matching the kernel route's
   * `requireString(ctx, 'universalKey')`.
   *
   * No `secretKey`. The three published proving fixtures are all `protection=public`, and a public
   * version needs no secret to install; a protected one would, and this client would have to grow a
   * further argument to carry it. Left out rather than threaded through as an always-undefined
   * parameter, because an optional secret that nothing ever supplies is indistinguishable from one
   * that is being dropped on the way.
   *
   * The interface's `packageId` is deliberately NOT sent. The Engine resolves the key to a package
   * and returns `packageKey` in its answer; sending a caller's opinion of which package a key names
   * would invite a request where the two disagree, and nothing good can be done with that. It
   * exists on the interface for the in-memory clients — see the note there.
   */
  async installByKey(universalKey: string): Promise<InstallOutcome> {
    return postInstall(PACKAGES_INSTALL_BY_KEY_PATH, { universalKey });
  },

  /**
   * CAOS-401 — the same fail-soft reading, for the preview.
   *
   * Every failure becomes an `ok:false` outcome rather than a thrown exception, for the same
   * reason `install` does it: the caller here is a render path, and a screen that cannot say what
   * an app requires should say less, never fail to draw.
   */
  async preview(packageId: string): Promise<PreviewOutcome> {
    let response: Response;
    try {
      response = await fetch(PACKAGES_INSTALL_PREVIEW_PATH, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ packageId }),
      });
    } catch (err) {
      return {
        ok: false,
        code: 'preview.unreachable',
        error: `Could not reach the install endpoint (${err instanceof Error ? err.message : String(err)}).`,
      };
    }

    let envelope: PreviewEnvelope;
    try {
      envelope = (await response.json()) as PreviewEnvelope;
    } catch {
      return {
        ok: false,
        code: 'preview.bad_response',
        error: `The install endpoint returned a non-JSON response (HTTP ${response.status}).`,
      };
    }

    if (!envelope || envelope.ok !== true) {
      const detail =
        envelope && envelope.ok === false && envelope.error?.message
          ? envelope.error.message
          : `HTTP ${response.status}`;
      const code = envelope && envelope.ok === false ? envelope.error?.code : undefined;
      return { ok: false, code, error: `Could not read what this needs (${detail}).` };
    }

    // An older kernel that answers the route but carries no `prerequisites` reads as an empty
    // list, which draws nothing — the same as an absent line, and never an invented one.
    return { ok: true, prerequisites: envelope.data?.prerequisites ?? [] };
  },
};

let client: InstallClient = httpInstallClient;

/** Override the install boundary (dev fixture / tests). */
export function setInstallClient(next: InstallClient): void {
  client = next;
}

/** The active install boundary — the picker's default when no client is injected. */
export function getInstallClient(): InstallClient {
  return client;
}
